import React from 'react';
import { 
  CompositeCard, 
  CardType, 
  CardData, 
  CardContext, 
  CardLocation,
  CardMetadata,
  CardTrigger,
  CardFilters,
  ContentCardType,
  ContextCardType
} from '../../../types/compositeCards';
import { CardLayoutFactory } from './CardLayoutFactory';
import { cardRegistry } from '../Registry/CardComponentRegistry';
import { v4 as uuidv4 } from 'uuid';
import { ArtifactProperty } from '../../../types/artifactItems';

export class CompositeCardFactory {
  /**
   * Create a composite card based on type, data, and context
   * Properties from the artifact data will be used to determine card sections
   */
  static createCard(
    type: CardType,
    data: CardData,
    context: CardContext,
    location: CardLocation = CardLocation.CONTENT
  ): CompositeCard {
    // Extract properties from artifact data if available
    const properties = this.extractPropertiesFromData(data);
    
    const layout = CardLayoutFactory.createLayout(type, context.uiRole, context.modelType, properties);
    
    const metadata: CardMetadata = {
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: context.userId,
      version: 1,
    };

    const triggers: CardTrigger[] = this.createDefaultTriggers(type, context);

    return {
      id: uuidv4(),
      location,
      type,
      context,
      data,
      metadata,
      layout,
      triggers,
      isCollapsed: false,
      isSelected: false,
      isPinned: false,
    };
  }

  /**
   * Create default triggers for card type and context
   */
  private static createDefaultTriggers(type: CardType, context: CardContext): CardTrigger[] {
    const triggers: CardTrigger[] = [];

    // Common triggers
    triggers.push({
      id: 'show-context',
      type: 'click',
      action: 'show_context',
      target: 'context-panel',
    });

    // Type-specific triggers
    switch (type) {
      case 'artifact':
        triggers.push({
          id: 'navigate-artifact',
          type: 'click',
          action: 'navigate',
          target: 'artifact-detail',
        });
        break;
      case 'artifact_item':
        triggers.push({
          id: 'transition-item',
          type: 'click',
          action: 'execute_command',
          target: '/transition',
        });
        break;
      case 'process':
        triggers.push({
          id: 'execute-process',
          type: 'click',
          action: 'execute_command',
          target: '/execute',
        });
        break;
      case 'list':
        triggers.push({
          id: 'filter-list',
          type: 'click',
          action: 'show_context',
          target: 'filter-panel',
        });
        break;
    }

    // Role-specific triggers
    switch (context.uiRole) {
      case 'architect':
        triggers.push({
          id: 'edit-mode',
          type: 'click',
          action: 'execute_command',
          target: '/edit',
        });
        break;
      case 'operator':
        triggers.push({
          id: 'monitor-mode',
          type: 'click',
          action: 'show_context',
          target: 'monitoring-panel',
        });
        break;
    }

    return triggers;
  }

  /**
   * Update an existing card
   */
  static updateCard(
    card: CompositeCard,
    updates: Partial<CompositeCard>
  ): CompositeCard {
    return {
      ...card,
      ...updates,
      metadata: {
        ...card.metadata,
        ...updates.metadata,
        updatedAt: new Date().toISOString(),
        version: card.metadata.version + 1,
      },
    };
  }

  /**
   * Create a context card from a content card
   */
  static createContextCard(
    contentCard: CompositeCard,
    contextType: ContextCardType
  ): CompositeCard {
    const contextCard = this.createCard(
      contextType,
      contentCard.data,
      contentCard.context,
      CardLocation.CONTEXT
    );

    // Add trigger to link back to content card
    contextCard.triggers.push({
      id: 'link-to-content',
      type: 'click',
      action: 'show_context',
      target: contentCard.id,
    });

    return contextCard;
  }

  /**
   * Filter cards based on criteria
   */
  static filterCards(cards: CompositeCard[], filters: CardFilters): CompositeCard[] {
    return cards.filter(card => {
      // Location filter
      if (filters.location && card.location !== filters.location) {
        return false;
      }

      // Type filter
      if (filters.type && card.type !== filters.type) {
        return false;
      }

      // UI Role filter
      if (filters.uiRole && card.context.uiRole !== filters.uiRole) {
        return false;
      }

      // Model Type filter
      if (filters.modelType && card.context.modelType !== filters.modelType) {
        return false;
      }

      // Pinned filter
      if (filters.isPinned !== undefined && card.isPinned !== filters.isPinned) {
        return false;
      }

      // Tags filter
      if (filters.tags && filters.tags.length > 0) {
        const cardTags = card.metadata.tags || [];
        const hasMatchingTag = filters.tags.some(tag => cardTags.includes(tag));
        if (!hasMatchingTag) {
          return false;
        }
      }

      return true;
    });
  }

  /**
   * Sort cards by various criteria
   */
  static sortCards(
    cards: CompositeCard[],
    sortBy: 'created' | 'updated' | 'name' | 'type' | 'pinned' = 'updated',
    order: 'asc' | 'desc' = 'desc'
  ): CompositeCard[] {
    return [...cards].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'created':
          comparison = new Date(a.metadata.createdAt).getTime() - new Date(b.metadata.createdAt).getTime();
          break;
        case 'updated':
          comparison = new Date(a.metadata.updatedAt).getTime() - new Date(b.metadata.updatedAt).getTime();
          break;
        case 'name': {
          const nameA = this.getCardName(a);
          const nameB = this.getCardName(b);
          comparison = nameA.localeCompare(nameB);
          break;
        }
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'pinned':
          if (a.isPinned && !b.isPinned) comparison = -1;
          else if (!a.isPinned && b.isPinned) comparison = 1;
          else comparison = 0;
          break;
      }

      return order === 'asc' ? comparison : -comparison;
    });
  }

  /**
   * Get card name for sorting
   */
  private static getCardName(card: CompositeCard): string {
    if ('artifact' in card.data) {
      return card.data.artifact.name;
    }
    if ('process' in card.data) {
      return card.data.process.name;
    }
    if ('artifactItem' in card.data) {
      return card.data.artifactItem.name;
    }
    if ('items' in card.data) {
      return `${card.data.itemType} List`;
    }
    return 'Unknown Card';
  }

  /**
   * Validate card data
   */
  static validateCard(card: CompositeCard): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Required fields
    if (!card.id) {
      errors.push('Card ID is required');
    }
    if (!card.type) {
      errors.push('Card type is required');
    }
    if (!card.context) {
      errors.push('Card context is required');
    }
    if (!card.data) {
      errors.push('Card data is required');
    }

    // Context validation
    if (card.context) {
      if (!card.context.threadId) {
        errors.push('Thread ID is required in context');
      }
      if (!card.context.userId) {
        errors.push('User ID is required in context');
      }
      if (!card.context.uiRole) {
        errors.push('UI Role is required in context');
      }
      if (!card.context.modelType) {
        errors.push('Model Type is required in context');
      }
    }

    // Data validation based on type
    if (card.data) {
      switch (card.type) {
        case 'artifact':
          if (!('artifact' in card.data) || !card.data.artifact) {
            errors.push('Artifact data is required for artifact cards');
          }
          break;
        case 'process':
          if (!('process' in card.data) || !card.data.process) {
            errors.push('Process data is required for process cards');
          }
          break;
        case 'list':
          if (!('items' in card.data) || !Array.isArray(card.data.items)) {
            errors.push('Items array is required for list cards');
          }
          break;
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Clone a card with new ID and updated metadata
   */
  static cloneCard(card: CompositeCard, newContext?: Partial<CardContext>): CompositeCard {
    const clonedCard = { ...card };
    clonedCard.id = uuidv4();
    clonedCard.metadata = {
      ...card.metadata,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
    };

    if (newContext) {
      clonedCard.context = { ...card.context, ...newContext };
    }

    return clonedCard;
  }

  /**
   * Get card summary for display
   */
  static getCardSummary(card: CompositeCard): string {
    const name = this.getCardName(card);
    const type = card.type;
    const role = card.context.uiRole;
    const model = card.context.modelType;
    
    return `${name} (${type}) - ${role} view in ${model} model`;
  }

  /**
   * Extract artifact properties from card data
   * Properties can be found in artifact.properties or artifact.artifact_properties
   */
  private static extractPropertiesFromData(data: CardData): ArtifactProperty[] | undefined {
    // Check for artifact in data
    if ('artifact' in data && data.artifact) {
      const artifact = data.artifact as any;
      
      // Properties might be in different locations
      if (artifact.properties && Array.isArray(artifact.properties)) {
        return artifact.properties as ArtifactProperty[];
      }
      if (artifact.artifact_properties && Array.isArray(artifact.artifact_properties)) {
        return artifact.artifact_properties as ArtifactProperty[];
      }
      // Check if properties are nested in metadata
      if (artifact.artifact_metadata?.properties && Array.isArray(artifact.artifact_metadata.properties)) {
        return artifact.artifact_metadata.properties as ArtifactProperty[];
      }
    }
    
    // Check for primeArtifact in data
    if ('primeArtifact' in data && data.primeArtifact) {
      const primeArtifact = data.primeArtifact as any;
      
      if (primeArtifact.properties && Array.isArray(primeArtifact.properties)) {
        return primeArtifact.properties as ArtifactProperty[];
      }
      if (primeArtifact.prime_artifact_properties && Array.isArray(primeArtifact.prime_artifact_properties)) {
        return primeArtifact.prime_artifact_properties as ArtifactProperty[];
      }
    }
    
    return undefined;
  }
}
